import {
  CalendarClock, FileText, MessageSquareText, MessagesSquare, Mic, Pencil, PhoneIncoming, PhoneOutgoing, Trash2,
  Voicemail,
} from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Callout, EmptyState, ErrorState, Spinner } from "@/components/ui/feedback";
import { Textarea } from "@/components/ui/input";
import { Tooltip } from "@/components/ui/misc";
import { useToast } from "@/components/ui/toast";
import { OutcomeTag } from "@/components/ui/outcome";
import { CadenceBadge } from "@/features/cadence-badge";
import { CallMarkBadge } from "@/features/call-mark";
import { CallTypeChip, leadCallType } from "@/features/call-type";
import { CallbackBackupDialog } from "@/features/callback-backup";
import { PersonName } from "@/features/person-card";
import { NextAppointment } from "@/features/upcoming-appointments";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { seesNextCall } from "@/lib/cadence";
import {
  backupLine, callbackCallText, callbackStatus, callbackWhen, canChangeBackup, knownZone, untilClock, whoCalls,
} from "@/lib/callbacks";
import { handoffHeadline } from "@/lib/closer-ask";
import { outcomeGroup, recordedOutcome, type OutcomeGroup } from "@/lib/dispositions";
import {
  contactName, noteTitle, ownerChangeText, stageChangeText, systemEventText, textMessageWords, timelineNoteLines,
} from "@/lib/contacts";
import { messageActivity } from "@/lib/conversations-from-api";
import { ownersKnown } from "@/lib/tasks";
import { groupByDay, timeOfDay } from "@/lib/timeline-days";
import { useNow } from "@/lib/use-now";
import { seesAutomation, workflowSender } from "@/lib/texts";
import { callText, humanize, outcomeText, voicemailLeftText } from "@/lib/lead-card";
import {
  useAddNote, useCallDispositions, useContactNotes, useContactTimeline, useEditNote, useOutcomeHistory,
  useRemoveNote, useTimelineFollowsConversation,
} from "@/lib/queries";
import { seesCampaigns,
  atLeast, type CallVoicemail, type LeadCallback, type LeadCallbackNoShow, type LeadCard, type LeadCardCall,
  type LeadHandoffConversation, type LeadNote, type LeadTimelineEntry, type TextMessage,
} from "@/lib/types";
import { cn, formatDateTime, formatPhoneNumber, relativeTime } from "@/lib/utils";
import { EmailLine } from "@/features/conversations/email-dialog";

/**
 * The record's Timeline and Notes tabs (ADR-044).
 *
 * The timeline is one list, newest first, from several tables at once — calls, notes, stage moves,
 * callbacks, texts, asks for a closer with what their setter and closer said after them (platform
 * ADR-189), and the platform's own events — merged by core-api and paged back through
 * `next_before`.
 * The chips narrow it server-side (`kinds=`), so "Notes" on a lead with a thousand calls is one small
 * request. The note composer sits at the top of both tabs: a note is one keystroke away.
 *
 * A callback (ADR-080) sits in the list at the moment it was promised, with where it stands now; the
 * one still to be made is also pinned above everything, on the lead's clock, so nobody opening the
 * record misses a promise.
 *
 * A text (ADR-095) is shown with which of our numbers it was sent to. An email (ADR-180) is one line
 * that opens the whole of it, as in Conversations. The timeline only reads them: opening a
 * conversation, and answering one, belong to Conversations.
 *
 * Each entry is a headline, not the whole of what happened (ACL-625), so the timeline scans as an
 * overview: what was said — a text's words, a handoff's brief, the talk after an ask for a closer, a
 * voicemail's words, a callback's note, the rest of a long note — opens in a card on hover or focus.
 *
 * A note typed on the record is the writer's, or a supervisor's, to change; a note that came with an
 * outcome and a handoff's brief are what was said at the time, and core-api refuses to change them —
 * the controls follow it.
 */

const KINDS = [
  { key: "", label: "All" },
  { key: "call", label: "Calls" },
  { key: "note", label: "Notes" },
  { key: "stage", label: "Stage" },
  { key: "callback", label: "Callbacks" },
  { key: "message", label: "Messages" },
  { key: "handoff", label: "Handoffs" },
  { key: "owner", label: "Owner" },
  { key: "system", label: "System" },
] as const;

/** The outcome labels a campaign uses, for a call's or a note's slug. Asks nothing until the contact
 *  is loaded; until then every slug is unknown, and shown in plain words. */
export function useOutcomeLabel(contact: LeadCard | undefined): (slug: string) => string | undefined {
  const outcomes = useCallDispositions(contact?.calling_campaign?.id ?? null, contact !== undefined);
  // A switched-off outcome on an old record still reads by its label (ACL-574).
  const history = useOutcomeHistory(contact !== undefined);
  return React.useCallback(
    (slug: string) => recordedOutcome(slug, outcomes.data, history.data)?.label,
    [outcomes.data, history.data],
  );
}

/** The group of an outcome's slug, for its colour (ADR-160), from the same list as its label. */
export function useOutcomeGroupOf(contact: LeadCard | undefined): (slug: string | null) => OutcomeGroup | null {
  const outcomes = useCallDispositions(contact?.calling_campaign?.id ?? null, contact !== undefined);
  const history = useOutcomeHistory(contact !== undefined);
  return React.useCallback(
    (slug: string | null) => {
      const found = recordedOutcome(slug, outcomes.data, history.data);
      return found ? outcomeGroup(found) : null;
    },
    [outcomes.data, history.data],
  );
}

export function NoteComposer({ contact, autoFocus }: { contact: LeadCard; autoFocus?: boolean }) {
  const toast = useToast();
  const add = useAddNote();
  const [body, setBody] = React.useState("");
  const id = React.useId();
  const wrap = React.useRef<HTMLFormElement>(null);

  // `Textarea` takes no ref; the form around it does, and the field is its one textarea.
  React.useEffect(() => {
    if (autoFocus) wrap.current?.querySelector("textarea")?.focus();
  }, [autoFocus]);

  const save = () => {
    const text = body.trim();
    if (!text) return;
    add.mutate(
      { leadId: contact.id, body: text },
      {
        onSuccess: () => {
          setBody("");
          toast.success("Note added");
        },
        onError: (e) =>
          toast.error("Could not add the note", e instanceof ApiError ? e.message : "Please try again."),
      },
    );
  };

  return (
    <form
      ref={wrap}
      className="space-y-2"
      onSubmit={(e) => {
        e.preventDefault();
        save();
      }}
    >
      <label htmlFor={id} className="sr-only">
        Add a note
      </label>
      <Textarea
        id={id}
        rows={2}
        maxLength={4000}
        value={body}
        placeholder="Add a note… (⌘↵ to save)"
        className="min-h-15 font-sans text-sm"
        onChange={(e) => setBody(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
            e.preventDefault();
            save();
          }
        }}
      />
      <div className="flex justify-end">
        <Button type="submit" size="sm" loading={add.isPending} disabled={!body.trim()}>
          Add note
        </Button>
      </div>
    </form>
  );
}

/**
 * A timeline entry that keeps more than its headline shows (ACL-625): pointing at it, or tabbing to
 * it, opens the rest in a card beside it. `more` is asked as the card is about to open, for words
 * that only sometimes run past their lines; an entry with nothing more is a plain block.
 */
function EntryDetails({
  details, more, className, children,
}: {
  details: React.ReactNode;
  more?: () => boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(false);
  if (!details) return <div className={cn("min-w-0", className)}>{children}</div>;
  return (
    <Tooltip
      variant="card"
      side="left"
      open={open}
      onOpenChange={(next) => setOpen(next && (more?.() ?? true))}
      content={<div className="max-h-80 w-max max-w-sm overflow-y-auto px-3 py-2.5 text-sm text-text">{details}</div>}
    >
      <div
        tabIndex={0}
        className={cn(
          "min-w-0 rounded-sm outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus",
          className,
        )}
      >
        {children}
      </div>
    </Tooltip>
  );
}

/** One note, with its writer and — for a note typed on the record — Edit and Remove. As a `headline`
 *  (the Timeline's, ACL-625) its words are cut to a line or two, and a handoff's brief to its title;
 *  the whole note opens on hover. */
export function NoteItem({
  note, contact, label, headline = false,
}: {
  note: LeadNote;
  contact: LeadCard;
  label: (slug: string) => string | undefined;
  headline?: boolean;
}) {
  const { user } = useAuth();
  const toast = useToast();
  const edit = useEditNote();
  const remove = useRemoveNote();
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(note.body);
  const [removing, setRemoving] = React.useState(false);
  const mine = user?.id === note.by_user_id;
  const changeable = note.kind === "note" && (mine || atLeast(user?.role, "supervisor"));
  const sees = seesAutomation(user?.role);
  const edited = note.updated_at !== note.created_at;
  const lines = headline ? timelineNoteLines(note) : null;
  const cut = React.useRef<HTMLParagraphElement>(null);
  // A brief is never shown, so always has more; a cut note has more only when its words run past.
  const more = () => lines === 0 || (cut.current !== null && cut.current.scrollHeight > cut.current.clientHeight);

  const save = () => {
    const text = draft.trim();
    if (!text || text === note.body) {
      setEditing(false);
      return;
    }
    edit.mutate(
      { leadId: contact.id, noteId: note.id, body: text },
      {
        onSuccess: () => setEditing(false),
        onError: (e) =>
          toast.error("Could not change the note", e instanceof ApiError ? e.message : "Please try again."),
      },
    );
  };

  return (
    <EntryDetails
      className="group/note"
      details={lines !== null && !editing && <p className="whitespace-pre-wrap break-words">{note.body}</p>}
      more={more}
    >
      <div className="flex items-baseline justify-between gap-2">
        <p className="text-sm text-text-muted">
          <span className="font-medium text-text">{noteTitle(note, label, sees)}</span>
          {note.by && (
            <>
              {" by "}
              <PersonName userId={note.by_user_id} name={note.by} />
            </>
          )}
          {/* A workflow's note names the workflow only to a supervisor and up (ACL-591). */}
          {note.workflow
            ? sees && ` · ${workflowSender(note.workflow)}`
            : note.author === "system" && !note.by && " · the platform"}
          {note.author === "trigger" && " · sent by an integration"}
        </p>
        {/* Held to the line's height, so a note's head reads as every other entry's; with a pointer, shown
            when the note is pointed at or a key is in it. */}
        <span className="-my-1.5 flex shrink-0 items-center gap-0.5 transition-opacity focus-within:opacity-100 group-hover/note:opacity-100 [@media(hover:hover)]:opacity-0">
          {changeable && !editing && (
            <>
              <Button variant="ghost" size="icon-sm" aria-label="Edit note" onClick={() => setEditing(true)}>
                <Pencil />
              </Button>
              <Button variant="ghost" size="icon-sm" aria-label="Remove note" onClick={() => setRemoving(true)}>
                <Trash2 />
              </Button>
            </>
          )}
        </span>
      </div>
      {editing ? (
        <form
          className="mt-1 space-y-2"
          onSubmit={(e) => {
            e.preventDefault();
            save();
          }}
        >
          <Textarea
            autoFocus
            rows={3}
            maxLength={4000}
            value={draft}
            className="font-sans text-sm"
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={() => { setDraft(note.body); setEditing(false); }}>
              Cancel
            </Button>
            <Button type="submit" size="sm" loading={edit.isPending} disabled={!draft.trim()}>
              Save
            </Button>
          </div>
        </form>
      ) : lines === null ? (
        <p className="mt-0.5 whitespace-pre-wrap text-sm text-text">{note.body}</p>
      ) : lines > 0 ? (
        <p
          ref={cut}
          className={cn("mt-0.5 whitespace-pre-wrap break-words text-sm text-text", lines === 1 ? "line-clamp-1" : "line-clamp-2")}
        >
          {note.body}
        </p>
      ) : null}
      {edited && !editing && <p className="mt-0.5 text-2xs text-text-subtle">edited {relativeTime(note.updated_at)}</p>}
      <ConfirmDialog
        open={removing}
        onOpenChange={setRemoving}
        title="Remove this note?"
        description="It leaves the record for good. An outcome's note and a handoff's brief cannot be removed."
        confirmLabel="Remove"
        destructive
        loading={remove.isPending}
        onConfirm={() =>
          remove.mutate(
            { leadId: contact.id, noteId: note.id },
            {
              onSuccess: () => setRemoving(false),
              onError: (e) =>
                toast.error("Could not remove the note", e instanceof ApiError ? e.message : "Please try again."),
            },
          )
        }
      />
    </EntryDetails>
  );
}

function CallEntry({
  entry, label, group,
}: {
  entry: LeadTimelineEntry;
  label: (slug: string) => string | undefined;
  group: (slug: string | null) => OutcomeGroup | null;
}) {
  const call = entry.call!;
  const inbound = call.direction === "inbound";
  const Icon = inbound ? PhoneIncoming : PhoneOutgoing;
  const sees = seesCampaigns(useAuth().user?.role);
  // An inbound call and a dialer call as every screen marks them (ACL-508); a call of neither,
  // a number a rep dialed by hand, keeps its direction's words.
  const type = leadCallType(call);
  // What a greeting written as text said to the lead, as core-api shows it to this reader, opens on
  // hover; a recording has no words, and nothing more.
  const said = call.voicemail?.words;
  return (
    <EntryDetails details={said && <blockquote className="whitespace-pre-wrap break-words">“{said}”</blockquote>}>
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-text">
        {type ? (
          <CallTypeChip type={type} size="sm" />
        ) : (
          <>
            <Icon className={cn("size-3.5 shrink-0", inbound ? "text-success-11" : "text-accent-11")} aria-hidden />
            <span className="font-medium">{inbound ? "Inbound call" : "Outbound call"}</span>
          </>
        )}
        <span className="text-text-muted">
          {type ? "" : "· "}
          <CallWords call={call} label={label} />
          {!type && call.via === "dialer" ? " · dialer" : ""}
        </span>
        {call.disposition && (
          <OutcomeTag group={group(call.disposition)} title="The outcome recorded for this call">
            {outcomeText(call.disposition, label)}
          </OutcomeTag>
        )}
        <CallMarkBadge call={call} />
        {call.has_recording && (
          <Tooltip content="Recorded. The Calls tab plays it.">
            <Mic className="size-3.5 text-accent-11" aria-label="Recorded" />
          </Tooltip>
        )}
        {call.has_transcript && (
          <Tooltip content="Transcribed. The Calls tab opens it.">
            <FileText className="size-3.5 text-success-11" aria-label="Transcribed" />
          </Tooltip>
        )}
      </p>
      {sees && (
        <p className="text-xs text-text-muted">
          {[call.campaign_name, call.queue ? `line ${call.queue}` : null].filter(Boolean).join(" · ")}
        </p>
      )}
      {call.callback && <p className="text-xs font-medium text-accent-11">{callbackCallText(call)}</p>}
      {call.voicemail && <VoicemailLeft voicemail={call.voicemail} />}
    </EntryDetails>
  );
}

/** A call's line, with the rep who talked named as a person a supervisor can hover. */
function CallWords({ call, label }: { call: LeadCardCall; label: (slug: string) => string | undefined }) {
  const text = callText(call, label, { withOutcome: false });
  const tail = call.rep_name ? ` with ${call.rep_name}` : null;
  if (!tail || !text.endsWith(tail)) return <>{text}</>;
  return (
    <>
      {text.slice(0, -call.rep_name!.length)}
      <PersonName phone={call.rep} name={call.rep_name!} />
    </>
  );
}

/**
 * The message a call left on a voicemail (ADR-128 in `platform`): which one, and whether it went. The
 * words a greeting said open with the call's other details on hover (ACL-625).
 */
function VoicemailLeft({ voicemail }: { voicemail: CallVoicemail }) {
  const said = voicemailLeftText(voicemail);
  return (
    <div className="mt-1.5 rounded-md border border-border bg-surface-sunken px-2.5 py-1.5">
      <p className="flex items-center gap-1.5 text-xs text-text">
        <Voicemail className="size-3.5 shrink-0 text-text-muted" aria-hidden />
        <span className={cn("font-medium", said.failed && "text-danger-11")}>{said.title}</span>
        {said.by && <span className="text-text-muted">· {said.by}</span>}
      </p>
    </div>
  );
}

/** A text, as its headline: its words open on hover (ACL-625). A STOP is marked: it is what
 *  made the lead do-not-call. One we sent (ADR-098) says who sent it and where it stands, and a
 *  failed one says why. To a supervisor and up one a workflow sent names it, and one a cadence sent
 *  is marked "Cadence" (ADR-135 in platform); a rep is told neither (ACL-591). An email is its one
 *  line, which opens it whole. */
function MessageEntry({ message, entryKey, contact }: { message: TextMessage; entryKey: string; contact: LeadCard }) {
  const sees = seesAutomation(useAuth().user?.role);
  if (message.channel === "email") {
    return (
      <div className="min-w-0 flex-1">
        <EmailLine
          message={messageActivity(message, null, entryKey)}
          name={contactName(contact) ?? "this contact"}
          address={contact.email}
          live
        />
      </div>
    );
  }
  const inbound = message.direction === "inbound";
  const words = textMessageWords(message, sees);
  const failed = words.why !== null;
  return (
    <EntryDetails details={message.body && <p className="whitespace-pre-wrap break-words">{message.body}</p>}>
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-text">
        <MessageSquareText
          className={cn(
            "size-3.5 shrink-0",
            inbound ? "text-success-11" : failed ? "text-danger-11" : "text-accent-11",
          )}
          aria-hidden
        />
        <span className="font-medium">{words.title}</span>
        <span className="text-text-muted">
          · {inbound ? "to" : "from"} {formatPhoneNumber(words.ourNumber)}
          {words.by &&
            (message.sent_by ? (
              <>
                {" · by "}
                <PersonName userId={message.sent_by.id} name={words.by} />
              </>
            ) : (
              ` · by ${words.by}`
            ))}
        </span>
        {!inbound && sees && message.sent_by_cadence && <CadenceBadge cadence={message.sent_by_cadence} />}
        {words.status && !failed && (
          <Badge size="sm" tone={words.status.tone}>
            {words.status.label}
          </Badge>
        )}
        {message.opt_out && (
          <Badge size="sm" tone="danger">
            STOP
          </Badge>
        )}
        {words.pictures && (
          <Badge size="sm" tone="outline">
            {words.pictures}
          </Badge>
        )}
      </p>
      {words.why && <p className="mt-0.5 text-xs text-danger-11">{words.why}</p>}
      {words.waits && <p className="mt-0.5 text-xs text-text-muted">{words.waits}</p>}
    </EntryDetails>
  );
}

function StageEntry({ entry }: { entry: LeadTimelineEntry }) {
  const sees = seesAutomation(useAuth().user?.role);
  const words = stageChangeText(entry.stage!);
  return (
    <p className="text-sm text-text">
      <span className="font-medium">Stage </span>
      {words.title}
      {words.detail && <span className="text-text-muted"> · {words.detail}</span>}
      {entry.by && (
        <span className="text-text-muted">
          {" · "}
          <PersonName userId={entry.by_user_id} name={entry.by} />
        </span>
      )}
      {entry.workflow && sees && <span className="text-text-muted"> · {workflowSender(entry.workflow)}</span>}
    </p>
  );
}

/** How many of an ask's messages its card shows: the newest few. The rest are in Team Chat. */
const HANDOFF_SHOWN = 4;

/**
 * An ask for a closer about the lead (platform ADR-189), as its headline: who asked whom and how it
 * ended (ACL-625). The note the closer got, and what the setter and the closer said about the lead
 * after it in the lead's conversation, open on hover; its people answer it in Team Chat.
 */
function HandoffEntry({ handoff: h }: { handoff: LeadHandoffConversation }) {
  const { asked, outcome } = handoffHeadline(h);
  return (
    <EntryDetails details={(h.note || h.messages.length > 0) && <HandoffDetails handoff={h} />}>
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-text">
        <MessagesSquare className="size-3.5 shrink-0 text-accent-11" aria-hidden />
        <span className="font-medium">Ask for a closer</span>
        <span className="text-text-muted">
          · {asked} · {outcome}
        </span>
      </p>
      {h.member && h.chat_id !== null && (
        <Link to={`/messages/${h.chat_id}`} className="mt-1 inline-block text-xs text-accent-11 hover:underline">
          {h.messages.length > 0 ? "Answer in Team Chat" : "Talk about them in Team Chat"}
        </Link>
      )}
    </EntryDetails>
  );
}

/** An ask's card: the note the closer got, and the newest of what was said after it. */
function HandoffDetails({ handoff: h }: { handoff: LeadHandoffConversation }) {
  const shown = h.messages.slice(-HANDOFF_SHOWN);
  const earlier = h.messages.length - shown.length;
  return (
    <div className="space-y-2">
      {h.note && <p className="whitespace-pre-wrap break-words text-xs">{h.note}</p>}
      {shown.length > 0 && (
        <ol className="space-y-1 border-l-2 border-accent-6 pl-2.5" aria-label="What was said after it">
          {earlier > 0 && (
            <li className="text-xs text-text-muted">
              {earlier} earlier {earlier === 1 ? "message" : "messages"} in Team Chat
            </li>
          )}
          {shown.map((m) => (
            <li key={m.id}>
              <span className="font-medium">{m.sender_name ?? "Somebody who left"}</span>
              <span className="text-xs text-text-subtle"> · {formatDateTime(m.created_at)}</span>
              <p className="whitespace-pre-wrap break-words">{m.body}</p>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/** A change of the lead's owner (ACL-408), and who made it, or to a supervisor and up which workflow
 *  (ACL-591). */
function OwnerEntry({ entry }: { entry: LeadTimelineEntry }) {
  const sees = seesAutomation(useAuth().user?.role);
  return (
    <p className="text-sm text-text">
      <span className="font-medium">Owner </span>
      {ownerChangeText(entry.owner!)}
      {entry.by && (
        <span className="text-text-muted">
          {" · "}
          <PersonName userId={entry.by_user_id} name={entry.by} />
        </span>
      )}
      {entry.workflow && sees && <span className="text-text-muted"> · {workflowSender(entry.workflow)}</span>}
    </p>
  );
}

/** The lead's clock, as the dialer reads it, when this browser knows the zone. */
function leadZone(contact: LeadCard): string | null {
  return knownZone(contact.clock_timezone ?? contact.timezone);
}

function CallbackEntry({
  callback, contact, label, group,
}: {
  callback: LeadCallback;
  contact: LeadCard;
  label: (slug: string) => string | undefined;
  group: (slug: string | null) => OutcomeGroup | null;
}) {
  const when = callbackWhen(callback.due_at, leadZone(contact));
  const status = callbackStatus(callback, Date.now());
  const outcome = label(callback.disposition) ?? humanize(callback.disposition) ?? "Callback";
  const then = callback.closed_by ? label(callback.closed_by) ?? humanize(callback.closed_by) : null;
  // Its backup time and where that stands, or that it is the backup of a missed one (ACL-559).
  const backup = backupLine(callback, leadZone(contact));
  return (
    // The note typed with the outcome opens on hover (ACL-625).
    <EntryDetails details={callback.note && <p className="whitespace-pre-wrap break-words">Note: {callback.note}</p>}>
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-text">
        <CalendarClock className="size-3.5 shrink-0 text-accent-11" aria-hidden />
        <OutcomeTag group={group(callback.disposition)} size="md" title="The outcome that set the callback">
          {outcome}
        </OutcomeTag>
        {callback.purpose && <span>{callback.purpose}</span>}
        <span className="text-text-muted">{callback.purpose ? "· " : ""}call back {when.there}</span>
        <Badge size="sm" tone={status.tone}>
          {status.label}
          {status.detail && ` · ${status.detail}`}
        </Badge>
      </p>
      <p className="text-xs text-text-muted">
        {[
          when.yours,
          callback.rep && (
            <>
              kept for <PersonName name={callback.rep} />
            </>
          ),
          callback.by && (
            <>
              set by <PersonName userId={callback.by_user_id} name={callback.by} />
            </>
          ),
          !callback.picked && "the outcome's own wait",
          // Until when the cadence keeps trying for it with its own calls and redials (ACL-661).
          callback.until && `worked until ${untilClock(callback.until, callback.due_at, leadZone(contact))}`,
          status.label === "Done" && then && `then ${then}`,
          backup?.text,
        ]
          .filter(Boolean)
          .map((part, i) => (
            <React.Fragment key={i}>
              {i > 0 && " · "}
              {part}
            </React.Fragment>
          ))}
      </p>
      {callback.no_show?.number && callback.status === "missed" ? (
        <p className="mt-0.5 inline-block text-xs font-medium text-warning-11">
          Missed booking {callback.no_show.number} of {callback.no_show.limit}
        </p>
      ) : null}
    </EntryDetails>
  );
}

/** The callback still to be made, pinned above the timeline on the lead's clock: what it is for,
 *  the note typed with it, and who places the call, so nobody dials the lead by hand while the
 *  dialer is about to (ADR-162 in `platform`). Its backup time is said with it, and added or changed
 *  here; the callback the system booked from a missed one's backup says it is that (ACL-559). An
 *  admin's alone, as the Next call card is (ACL-642). */
export function OpenCallback({ contact }: { contact: LeadCard }) {
  const { user } = useAuth();
  const [changing, setChanging] = React.useState(false);
  const callback = contact.callback;
  if (!callback || !seesNextCall(user?.role)) return null;
  const who = whoCalls(callback, user?.id);
  const when = callbackWhen(callback.due_at, leadZone(contact));
  const status = callbackStatus(callback, Date.now());
  const due = callback.status === "due";
  const backup = backupLine(callback, leadZone(contact));
  const title = callback.backup_of ? "Backup callback" : "Callback";
  // Only a callback whose call the dialer will make can miss it; an older core-api keeps no backup.
  // A backup the system booked keeps the times after it as they were given (ADR-210 in `platform`).
  const action =
    callback.backup_state === "waiting" && !callback.backup_of
      ? "Change backup time"
      : canChangeBackup(callback) && !callback.backup_at
        ? "Add backup time"
        : null;
  return (
    <div
      role="status"
      className={cn(
        "flex items-start gap-2.5 rounded-lg border px-3 py-2.5",
        due ? "border-warning-4 bg-warning-2" : "border-accent-7 bg-accent-2",
      )}
    >
      <CalendarClock className={cn("mt-0.5 size-4 shrink-0", due ? "text-warning-11" : "text-accent-11")} aria-hidden />
      <div className="min-w-0 space-y-0.5">
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-sm text-text">
          <span className="font-medium">{due ? `${title} due` : title}</span>
          {callback.purpose && <span className="font-medium">· {callback.purpose}</span>}
          <span>{when.there}</span>
          <Badge size="sm" tone={status.tone}>
            {status.label}
            {status.detail && ` · ${status.detail}`}
          </Badge>
        </p>
        <p className="text-xs text-text-muted">
          {[
            when.yours,
            callback.until && `worked until ${untilClock(callback.until, callback.due_at, leadZone(contact))}`,
            callback.rep && `kept for ${callback.rep}`,
            callback.by && `set by ${callback.by}`,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
        {backup && (
          <p className={cn("text-xs", backup.tone === "warning" ? "text-warning-11" : "text-text-muted")}>{backup.text}</p>
        )}
        {callback.note && <p className="text-xs text-text-muted">Note: {callback.note}</p>}
        {callback.no_show?.missed ? <NoShowCount count={callback.no_show} /> : null}
        {who && <p className="text-xs font-medium text-text">{who}</p>}
        {action && (
          <button
            type="button"
            onClick={() => setChanging(true)}
            className="inline-flex items-center gap-1 text-2xs font-medium text-accent-11 hover:underline"
          >
            <CalendarClock className="size-3" aria-hidden />
            {action}
          </button>
        )}
      </div>
      {/* Mounted only while open, so its picker opens on the backup as it stands now. */}
      {changing && <CallbackBackupDialog contact={contact} callback={callback} onClose={() => setChanging(false)} />}
    </div>
  );
}

/** An open booking whose outcome has a no-show limit, once one has been missed (ADR-181): how many,
 *  and what the next misses do. */
function NoShowCount({ count }: { count: LeadCallbackNoShow }) {
  const missed = count.missed ?? 0;
  const outcome = count.outcome_label ?? count.outcome;
  return (
    <p className="flex items-center gap-1.5 pt-1 text-xs text-text">
      <Badge size="sm" tone="warning">
        {missed} of {count.limit} missed
      </Badge>
      <span className="text-text-muted">
        {missed + 1 >= count.limit
          ? `If this one is missed too: ${outcome}, no recovery call.`
          : `${count.limit - missed} more missed and the lead gets ${outcome}.`}
      </span>
    </p>
  );
}

function SystemEntry({ entry }: { entry: LeadTimelineEntry }) {
  return (
    <p className="text-sm text-text-muted">
      {systemEventText(entry.event!)}
      <span className="text-text-subtle"> · system</span>
    </p>
  );
}

function When({ at }: { at: string }) {
  return (
    <time dateTime={at} title={formatDateTime(at)} className="shrink-0 whitespace-nowrap text-xs text-text-subtle">
      {relativeTime(at)}
    </time>
  );
}

/**
 * The timeline as a history by day (ACL-508): each day under its heading ("Today", "Yesterday",
 * "Thu, Oct 15"), which stays at the top while its entries scroll, and every entry on one line down
 * the side at its time of day; the full date and time are its hover. Newest first, as core-api sends
 * them. The days are the viewer's, on this browser's clock; a day nothing is drawn for has no heading.
 */
function DayList<T>({
  items, at, keyOf, render,
}: {
  items: readonly T[];
  at: (item: T) => string;
  keyOf: (item: T) => string;
  /** An item's row, or null for one this console does not draw. */
  render: (item: T) => React.ReactNode;
}) {
  // Read again each minute, so a page left open past midnight moves "Today" to "Yesterday".
  const now = useNow(60_000);
  const days = groupByDay(items, at, new Date(now));
  return (
    <div className="space-y-2">
      {days.map((day) => {
        const rows = day.items.map((item) => ({ item, body: render(item) })).filter((r) => r.body);
        if (rows.length === 0) return null;
        return (
          <section key={day.key} aria-label={day.heading}>
            <h3 className="sticky top-0 z-10 bg-surface py-1.5 text-xs font-semibold text-text-muted">{day.heading}</h3>
            <ol className="relative">
              <span aria-hidden className="absolute top-0 bottom-0 left-[5px] w-px bg-border" />
              {rows.map(({ item, body }) => (
                <li key={keyOf(item)} className="relative flex items-start justify-between gap-4 py-2.5 pl-6">
                  <span
                    aria-hidden
                    className="absolute top-[15px] left-0 size-[11px] rounded-full border-2 border-border-strong bg-surface"
                  />
                  {body}
                  <TimeOfDay at={at(item)} />
                </li>
              ))}
            </ol>
          </section>
        );
      })}
    </div>
  );
}

/** An entry's time under its day's heading, with the whole date and time on hover. */
function TimeOfDay({ at }: { at: string }) {
  return (
    <time dateTime={at} title={formatDateTime(at)} className="shrink-0 whitespace-nowrap text-xs text-text-subtle tabular-nums">
      {timeOfDay(at)}
    </time>
  );
}

export function ContactTimeline({ contact, composeFocus }: { contact: LeadCard; composeFocus?: boolean }) {
  const [kind, setKind] = React.useState<string>("");
  const timeline = useContactTimeline(contact.id, kind);
  useTimelineFollowsConversation(contact.id);
  const label = useOutcomeLabel(contact);
  const group = useOutcomeGroupOf(contact);
  const entries = timeline.data?.pages.flatMap((p) => p.entries) ?? [];

  return (
    <div className="space-y-4">
      <OpenCallback contact={contact} />
      {/* What else is scheduled with them, each with its own day and time (ACL-559), pinned as a
          conversation's thread pins it. */}
      <NextAppointment key={`next:${contact.id}`} leadId={contact.id} />
      <NoteComposer contact={contact} autoFocus={composeFocus} />
      <div className="flex flex-wrap gap-x-1.5 gap-y-2.5" role="group" aria-label="Show">
        {/* "Owner" asks `kinds=owner`, which only a core-api with owners knows (ACL-408). */}
        {KINDS.filter((k) => k.key !== "owner" || ownersKnown(contact)).map((k) => (
          <button
            key={k.key}
            type="button"
            aria-pressed={kind === k.key}
            onClick={() => setKind(k.key)}
            className={cn(
              "rounded-full border px-3 py-1 text-sm leading-[18px] transition-colors",
              kind === k.key
                ? "border-accent-8 bg-accent-2 text-accent-11"
                : "border-border bg-surface text-gray-11 hover:bg-surface-hover",
            )}
          >
            {k.label}
          </button>
        ))}
      </div>
      {timeline.isPending ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-4" />
        </div>
      ) : timeline.error ? (
        <ErrorState error={timeline.error} title="Could not load the timeline" />
      ) : entries.length === 0 ? (
        <EmptyState title="Nothing here yet" description="Calls, notes, stage moves, callbacks, texts, emails, handoffs and the platform's own events appear here as they happen." />
      ) : (
        <DayList
          items={entries}
          at={(entry) => entry.at}
          keyOf={(entry) => entry.key}
          render={(entry) =>
            // An entry of a kind this console does not draw yet (core-api shipped one ahead of it) is
            // left out rather than shown as an empty row.
            entry.kind === "call" ? (
              <CallEntry entry={entry} label={label} group={group} />
            ) : entry.kind === "note" && entry.note ? (
              <NoteItem note={entry.note} contact={contact} label={label} headline />
            ) : entry.kind === "stage" ? (
              <StageEntry entry={entry} />
            ) : entry.kind === "callback" && entry.callback ? (
              <CallbackEntry callback={entry.callback} contact={contact} label={label} group={group} />
            ) : entry.kind === "message" && entry.message ? (
              <MessageEntry message={entry.message} entryKey={entry.key} contact={contact} />
            ) : entry.kind === "owner" && entry.owner ? (
              <OwnerEntry entry={entry} />
            ) : entry.kind === "handoff" && entry.handoff ? (
              <HandoffEntry handoff={entry.handoff} />
            ) : entry.event ? (
              <SystemEntry entry={entry} />
            ) : null
          }
        />
      )}
      {timeline.hasNextPage && (
        <div className="flex justify-center">
          <Button variant="outline" size="sm" loading={timeline.isFetchingNextPage} onClick={() => void timeline.fetchNextPage()}>
            Earlier
          </Button>
        </div>
      )}
    </div>
  );
}

export function ContactNotes({ contact }: { contact: LeadCard }) {
  const notes = useContactNotes(contact.id);
  const label = useOutcomeLabel(contact);
  return (
    <div className="space-y-4">
      <NoteComposer contact={contact} />
      {notes.isPending ? (
        <div className="flex justify-center py-8">
          <Spinner className="size-4" />
        </div>
      ) : notes.error ? (
        <ErrorState error={notes.error} title="Could not load the notes" />
      ) : notes.data.length === 0 ? (
        <EmptyState title="No notes yet" description="What a rep types here, with an outcome, or on a handoff, is kept on the record." />
      ) : (
        <ol className="divide-y divide-border">
          {notes.data.map((note) => (
            <li key={note.id} className="flex items-start justify-between gap-4 py-2.5">
              <NoteItem note={note} contact={contact} label={label} />
              <When at={note.created_at} />
            </li>
          ))}
        </ol>
      )}
      {notes.data && notes.data.some((n) => n.kind !== "note") && (
        <Callout tone="info">A note that came with an outcome, and a handoff's brief, are what was said at the time and cannot be changed.</Callout>
      )}
    </div>
  );
}
